import React, { useState, useContext, useRef, useCallback } from "react";
import { useAuthQuery as useQuery } from '../../../shared/hooks/useAuthQuery';
import { useMutation } from "convex/react";
import Markdown from "react-markdown";
import { api } from "../../../convex/_generated/api";
import { BaseContext } from "../../../shared/base-context";
import { Id } from "../../../convex/_generated/dataModel";
import { Button } from "../../../catalyst-components/button";
import { AppGuideChatPanel } from "./AppGuideChatPanel";

// ─── Slug helpers (must match backend in app_guide_actions.ts) ───

function slugifyHeading(headingText: string): string {
  return headingText
    .replace(/^#{1,6}\s*/, "")
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getTextFromChildren(children: React.ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(getTextFromChildren).join("");
  if (children && typeof children === "object" && "props" in children) {
    return getTextFromChildren((children as React.ReactElement).props.children);
  }
  return "";
}

// ─── Component ───────────────────────────────────────────

export const AppGuideTab: React.FC = () => {
  const { convexUserId } = useContext(BaseContext);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);
  const guideScrollRef = useRef<HTMLDivElement>(null);

  const guide = useQuery(api.app_guide.getGuide);
  const updateGuide = useMutation(api.app_guide.updateGuide);

  const handleEdit = () => {
    setEditContent(guide?.content || "");
    setIsEditing(true);
  };

  const handleSave = async () => {
    await updateGuide({
      content: editContent,
      user_id: convexUserId as Id<"users">,
    });
    setIsEditing(false);
  };

  // Fetch APP_GUIDE.md from public/ and save directly to DB
  const handleSyncFromFile = async () => {
    setIsSyncing(true);
    try {
      const response = await fetch("/APP_GUIDE.md");
      if (!response.ok) throw new Error("Failed to fetch APP_GUIDE.md");
      const content = await response.text();
      await updateGuide({
        content,
        user_id: convexUserId as Id<"users">,
      });
    } catch (err) {
      alert("Failed to sync from APP_GUIDE.md. Make sure the file exists in the public/ folder.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Scroll-to-section callback passed to chat panel
  const handleScrollToSection = useCallback((slug: string) => {
    const container = guideScrollRef.current;
    if (!container) return;

    const target = container.querySelector(`#${CSS.escape(slug)}`);
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "start" });

    // Flash highlight
    target.classList.add("guide-highlight");
    setTimeout(() => target.classList.remove("guide-highlight"), 2000);
  }, []);

  // Shared heading renderer with anchor ID
  const makeHeading = (
    Tag: "h1" | "h2" | "h3",
    className: string
  ) => {
    const HeadingComponent = ({ children }: { children?: React.ReactNode }) => {
      const text = getTextFromChildren(children);
      const id = slugifyHeading(text);
      return (
        <Tag id={id} className={`${className} scroll-mt-4 transition-colors duration-700`}>
          {children}
        </Tag>
      );
    };
    return HeadingComponent;
  };

  return (
    <div className="flex gap-6" style={{ height: "calc(100vh - 200px)" }}>
      {/* Highlight animation style */}
      <style>{`
        .guide-highlight {
          background-color: rgba(var(--color-primary-5-rgb, 99, 102, 241), 0.15);
          border-radius: 4px;
        }
      `}</style>

      {/* Left panel: Guide content */}
      <div className="flex-1 flex flex-col overflow-hidden border rounded-lg bg-white">
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">App Guide</h2>
            {guide && (
              <p className="text-xs text-gray-500 mt-0.5">
                Version {guide.version} | Last modified: {new Date(guide.last_modified_timestamp).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button color="dark" onClick={handleSave}>Save</Button>
                <Button color="light" onClick={() => setIsEditing(false)}>Cancel</Button>
              </>
            ) : (
              <>
                <Button
                  color="light"
                  onClick={handleSyncFromFile}
                  disabled={isSyncing}
                >
                  {isSyncing ? "Syncing..." : "Sync from file"}
                </Button>
                <Button color="light" onClick={handleEdit}>
                  {guide?.content ? "Edit" : "Create Guide"}
                </Button>
              </>
            )}
          </div>
        </div>

        <div ref={guideScrollRef} className="flex-1 overflow-y-auto p-6">
          {!guide?.content && !isEditing ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-4">📖</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Guide Content Yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Create a comprehensive guide explaining how the app works.
                You can paste the content of APP_GUIDE.md from the project root.
              </p>
              <Button color="dark" onClick={handleEdit}>Create Guide</Button>
            </div>
          ) : isEditing ? (
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full h-full font-mono text-sm border rounded-lg p-4 resize-none focus:outline-none focus:ring-2 focus:ring-primary-5/50 focus:border-primary-5"
              placeholder="Paste or write your guide in Markdown format..."
            />
          ) : (
            <div className="prose prose-sm max-w-none">
              <Markdown
                components={{
                  h1: makeHeading("h1", "text-2xl font-bold text-gray-900 mt-6 mb-3 pb-2 border-b"),
                  h2: makeHeading("h2", "text-xl font-semibold text-gray-800 mt-5 mb-2"),
                  h3: makeHeading("h3", "text-lg font-semibold text-gray-700 mt-4 mb-2"),
                  p: ({ children }) => <p className="text-gray-700 leading-relaxed mb-3">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="text-gray-700">{children}</li>,
                  code: ({ children, className }) => {
                    if (className?.includes("language-")) {
                      return <code className={`${className} block`}>{children}</code>;
                    }
                    return (
                      <code className="bg-gray-100 text-primary-6 px-1.5 py-0.5 rounded text-sm font-mono">
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-3 text-sm font-mono [&>code]:bg-transparent [&>code]:text-inherit [&>code]:p-0 [&>code]:rounded-none [&>code]:text-sm">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-primary-5 pl-4 py-1 my-3 text-gray-600 italic">
                      {children}
                    </blockquote>
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-3">
                      <table className="min-w-full border-collapse border border-gray-200">{children}</table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="border border-gray-200 px-3 py-2 bg-gray-50 text-left text-sm font-semibold">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border border-gray-200 px-3 py-2 text-sm">{children}</td>
                  ),
                  hr: () => <hr className="my-6 border-gray-200" />,
                  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                }}
              >
                {guide?.content || ""}
              </Markdown>
            </div>
          )}
        </div>
      </div>

      {/* Right panel: Chat */}
      <div className="w-[400px] flex-shrink-0">
        <AppGuideChatPanel onScrollToSection={handleScrollToSection} />
      </div>
    </div>
  );
};
